import { useEffect, useState } from 'react';
import { gql, useQuery } from '@apollo/client';

import { useCurrentMemberContext } from 'src/contexts';
import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';

export const GET_EMPLOYEES = gql`
  query ($payload: GetEmployeesInput!) {
    getEmployees(payload: $payload) {
      collection {
        _id
        ganttId
        createdAt
        createdBy
        companyId
        firstName
        lastName
        email
        name
        avatarUrl
        nickname
        phoneNumber
        workingDays {
          startFrom
          startFromString
          monday {
            from
            to
          }
          tuesday {
            from
            to
          }
          wednesday {
            from
            to
          }
          thursday {
            from
            to
          }
          friday {
            from
            to
          }
          saturday {
            from
            to
          }
          sunday {
            from
            to
          }
        }
        gantt {
          name
        }
      }
      totalCount
    }
  }
`;

export const EMPLOYEES_CHANGED_SUBSCRIPTION = gql`
  subscription employeesChanged($ganttId: String!) {
    employeesChanged(ganttId: $ganttId) {
      trigger
      employee {
        _id
        avatarUrl
        nickname
        ganttId
        createdAt
        createdBy
        companyId
        firstName
        lastName
        email
        name
      }
    }
  }
`;

export const useGetEmployees = ({
  searchParam,
  companyId,
  ganttIds,
}: {
  searchParam: string;
  companyId?: string;
  ganttIds: string[];
}) => {
  const [currentPage, setPage] = useState(1);
  const [currentNameSort, setNameSort] = useState<{ field: string; sortBy: number }[]>();

  const pageSize = 12;
  const { data, loading, refetch } = useQuery(GET_EMPLOYEES, {
    variables: {
      payload: {
        queryOptions: {
          limit: pageSize,
          skip: pageSize * currentPage - pageSize,
          sort: currentNameSort,
        },
        filter: {
          companyId,
          ganttIds,
        },
        searchQuery: {
          searchParam,
        },
      },
    },
    skip: !companyId,
    fetchPolicy: 'cache-and-network',
  });

  const { totalCount = 0, collection = [] } = data?.getEmployees || {};

  useEffect(() => setPage(1), [searchParam]);

  return {
    loading,
    employees: collection,
    paginationConfig: {
      total: totalCount,
      pageSize,
      current: currentPage,
      onChange: (page: number) => setPage(page),
    },
    onTableChange: (a: any, b: any, { order }: any) => {
      if (order) {
        const sortBy = order === 'ascend' ? 1 : -1;

        setNameSort([{ field: 'firstName', sortBy }]);
      }
    },
    refetch,
  };
};

export const useGetAllEmployeesForActiveGantt = () => {
  const { ganttId } = useCurrentMemberContext();
  const companyId = useGetCurrentCompanyId();
  const { data, loading, subscribeToMore } = useQuery(GET_EMPLOYEES, {
    variables: {
      payload: {
        filter: { companyId, ganttIds: [ganttId] },
        queryOptions: { sort: [{ field: 'createdAt', sortBy: 1 }] },
      },
    },
    fetchPolicy: 'cache-and-network',
    skip: !ganttId,
  });

  useEffect(() => {
    if (ganttId) {
      subscribeToMore({
        document: EMPLOYEES_CHANGED_SUBSCRIPTION,
        variables: { ganttId },
        updateQuery: (prev, { subscriptionData }) => {
          if (!subscriptionData.data) return prev;

          const { trigger, employee } = subscriptionData.data?.employeesChanged || {};
          const { collection: employees = [], totalCount } = prev?.getEmployees || {};
          const newEmployees = [...employees];
          const findIndex = employees.findIndex((value: any) => value._id === employee._id);

          if (trigger === 'employeeCreated' && findIndex < 0) {
            newEmployees.push(employee);
          }

          if (findIndex > -1) {
            if (trigger === 'employeeRemoved') {
              newEmployees.splice(findIndex, 1);
            }

            if (trigger === 'employeeUpdated') {
              newEmployees[findIndex] = employee;
            }
          }

          return { getEmployees: { collection: newEmployees, totalCount } };
        },
      });
    }
  }, [subscribeToMore, ganttId]);

  const { collection = [] } = data?.getEmployees || {};

  return { loading, employees: collection };
};
